import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Link,
    Stack,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import TaskTrackingService, { taskErrorMessage } from '../../services/TaskTrackingService';
import WorkFileService from '../../services/WorkFileService';
import { CONTENT_TYPE_FOR, languageTracking } from './startTracking';
import TaskStatusChip from './TaskStatusChip';
import { shortName } from './taskUi';

/**
 * Manage task, from a file's three dots menu. PMs and admins see which languages have an open task
 * and can Start tracking. Videos also Save SRT to the language's shared folder: the server says
 * which languages this person sees and why a level can't be saved yet, so that reason shows here
 * and the same rules hold if a page is out of date.
 */
const ManageTaskDialog = ({ open, onClose, file, canStartTracking, onStartTracking }) => {
    useTrackUsage('ManageTaskDialog', 'dialog', 'tasks', open);
    // Open tasks of this file. Only PMs and admins load them, since others see just their own
    const [tasks, setTasks] = useState(null);
    const [view, setView] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(null);
    // { language, level, name, by }: the name is taken, so ask Replace or Keep both
    const [taken, setTaken] = useState(null);

    const isVideo = file?.type === 'video';
    const videoId = file?.data;

    const load = () => WorkFileService.srt(videoId).then(setView);

    useEffect(() => {
        if (!open || !file?.data) return;
        setTasks(null);
        setView(null);
        setError(null);
        setSaved(null);
        setTaken(null);
        if (canStartTracking) {
            TaskTrackingService.forContents([{ contentType: CONTENT_TYPE_FOR[file.type], contentId: file.data }])
                .then(setTasks)
                .catch((e) => setError(taskErrorMessage(e)));
        }
        if (isVideo) load().catch((e) => setError(taskErrorMessage(e)));
    }, [open, file?.data]); // eslint-disable-line react-hooks/exhaustive-deps

    const save = async (language, level, mode) => {
        setBusy(true);
        setError(null);
        setSaved(null);
        try {
            const f = await WorkFileService.saveSrt(videoId, { languageKey: language.languageKey, level, mode });
            setTaken(null);
            setSaved({ name: f.name, label: language.label, languageKey: language.languageKey });
        } catch (e) {
            const data = e?.response?.data;
            if (data?.code === 'NAME_TAKEN') {
                setTaken({ language, level, name: data.details?.name, by: shortName(data.details?.existingBy) });
            } else {
                setError(taskErrorMessage(e));
                // The language may have moved on since this was opened
                load().catch(() => {});
            }
        } finally {
            setBusy(false);
        }
    };

    const languages = view?.languages || [];
    const tracking = tasks ? languageTracking(file, tasks) : [];

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Manage task</DialogTitle>
            <DialogContent>
                {canStartTracking && (
                    <>
                        <Typography variant="subtitle2">Tracking</Typography>
                        {!tasks && !error && <CircularProgress size={24} />}
                        {tasks && tracking.length === 0 && (
                            <Typography variant="body2">This file has no languages yet.</Typography>
                        )}
                        {tracking.map(({ key, label, task }) => (
                            <Stack
                                key={key}
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={1}
                                sx={{ py: 0.5 }}
                                data-testid={`tracking-language-${key}`}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2">{label}</Typography>
                                    <Typography variant="caption" color="text.secondary" component="div">
                                        {task ? task.currentStageName : 'No open task'}
                                    </Typography>
                                </Box>
                                {task && <TaskStatusChip row={task} />}
                            </Stack>
                        ))}
                        <Button sx={{ mt: 0.5, mb: 1.5 }} variant="outlined" disabled={busy} onClick={onStartTracking}>
                            Start tracking
                        </Button>
                        {isVideo && <Divider sx={{ mb: 1.5 }} />}
                    </>
                )}
                {isVideo && (
                    <>
                        <Typography variant="subtitle2">Save SRT to Shared Folder</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Each language saves the subtitles as they were when that level was completed.
                        </Typography>
                        {!view && !error && <CircularProgress size={24} />}
                        {view && languages.length === 0 && (
                            <Typography variant="body2">No languages here for you to save.</Typography>
                        )}
                        {languages.map((language) => (
                            <Stack
                                key={language.languageKey}
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={1}
                                sx={{ py: 0.75, flexWrap: { xs: 'wrap', sm: 'nowrap' }, rowGap: 0.5 }}
                                data-testid={`srt-language-${language.languageKey}`}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2">{language.label}</Typography>
                                    <Typography variant="caption" color="text.secondary" component="div">
                                        {language.levels.find((l) => !l.ready)?.reason || language.stage}
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    {language.levels.map((l) => (
                                        <Button
                                            key={l.level}
                                            size="small"
                                            variant="outlined"
                                            disabled={busy || !l.ready}
                                            aria-label={`Save ${l.level} SRT for ${language.label}`}
                                            onClick={() => save(language, l.level)}
                                        >
                                            Save {l.level}
                                        </Button>
                                    ))}
                                </Stack>
                            </Stack>
                        ))}
                    </>
                )}
                {taken && (
                    <Alert
                        severity="warning"
                        sx={{ mt: 2 }}
                        action={
                            <Stack direction="row" spacing={1}>
                                <Button size="small" disabled={busy} onClick={() => save(taken.language, taken.level, 'replace')}>
                                    Replace
                                </Button>
                                <Button size="small" disabled={busy} onClick={() => save(taken.language, taken.level, 'keepBoth')}>
                                    Keep both
                                </Button>
                            </Stack>
                        }
                    >
                        {taken.language.label} already has {taken.name}
                        {taken.by ? `, added by ${taken.by}` : ''}.
                    </Alert>
                )}
                {saved && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        Saved {saved.name} in {saved.label}.{' '}
                        <Link href={`/work/video/${videoId}?lang=${saved.languageKey}`}>Open Shared Folder</Link>
                    </Alert>
                )}
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ManageTaskDialog;
